// src/index.service.ts
import io from "socket.io-client";
var Service = class {
  constructor(config) {
    this.environment = config.environment;
    this.getAccessToken = config.getAccessToken;
    this.onException = config.onException;
    this.setAccessToken = config.setAccessToken;
    this.getAccessToken = config.getAccessToken;
    this.setRefreshToken = config.setRefreshToken;
    this.getRefreshToken = config.getRefreshToken;
    this.clientVersion = config.clientVersion;
    switch (config.environment) {
      case "development":
        this.httpBaseUrl = "https://api.juhuu.dev/v1/";
        this.wssBaseUrl = "wss://api.juhuu.dev/v1/";
        break;
      default:
        this.httpBaseUrl = "https://api.juhuu.app/v1/";
        this.wssBaseUrl = "wss://api.juhuu.app/v1/";
        break;
    }
  }
  environment;
  httpBaseUrl;
  wssBaseUrl;
  clientVersion;
  onException;
  getAccessToken;
  setAccessToken;
  getRefreshToken;
  setRefreshToken;
  async sendRequest({
    url,
    method,
    body = void 0,
    authenticationNotOptional
  }, options = {}) {
    if (options.triggerOnException === void 0) {
      options.triggerOnException = true;
    }
    if (options.refreshTokensIfNecessary === void 0) {
      options.refreshTokensIfNecessary = true;
    }
    let token = null;
    if (options.accessToken === void 0 || options.accessToken === null) {
      token = await this.getAccessToken();
    } else {
      token = options.accessToken;
    }
    console.log("accessToken:", token);
    if ((token === null || token === void 0) && authenticationNotOptional === true) {
      console.error(
        "endpoint",
        url,
        "should use authentication but no token was found"
      );
      return {
        ok: false,
        data: null
      };
    }
    const uri = this.httpBaseUrl + url;
    let response = null;
    let responseObject = null;
    try {
      switch (method) {
        case "GET": {
          response = await this.sendGetRequest(token, uri);
          break;
        }
        case "POST": {
          response = await this.sendPostRequest(token, uri, body);
          break;
        }
        case "PATCH": {
          response = await this.sendPatchRequest(token, uri, body);
          break;
        }
        case "DELETE": {
          response = await this.sendDeleteRequest(token, uri);
          break;
        }
      }
      if (response.ok === false) {
        throw new Error(response.statusText);
      }
      responseObject = {
        ok: response.ok,
        data: await response.json(),
        statusText: response.statusText,
        status: response.status
      };
    } catch (error) {
      console.error(error);
      responseObject = {
        ok: false,
        data: await response?.json(),
        statusText: response?.statusText ?? "no statusText",
        status: response?.status ?? 0
      };
      if (responseObject.status === 403 && options.refreshTokensIfNecessary === true) {
        console.log("refreshing tokens...");
        const oldRefreshToken = await this.getRefreshToken();
        console.log("old refresh token", oldRefreshToken);
        if (oldRefreshToken === null) {
          console.log("no old refresh token found");
          return responseObject;
        }
        console.log("sending request to refresh tokens...");
        const query = await this.sendRequest(
          {
            method: "GET",
            url: "auth/refresh",
            body: void 0,
            authenticationNotOptional: true
          },
          {
            accessToken: oldRefreshToken,
            // use old refresh token instead of access token
            triggerOnException: false,
            refreshTokensIfNecessary: false
          }
        );
        console.log("query for new tokens", query);
        if (query.ok === false) {
          return responseObject;
        }
        const accessToken = query.data.accessToken;
        const refreshToken = query.data.refreshToken;
        await Promise.all([
          this.setRefreshToken(refreshToken),
          this.setAccessToken(accessToken)
        ]);
        console.log("retrying original request...");
        const retryResponse = await this.sendRequest(
          {
            url,
            method,
            body,
            authenticationNotOptional
          },
          {
            accessToken,
            refreshTokensIfNecessary: false
          }
        );
        console.log("retry response", retryResponse);
        if (retryResponse.ok === true) {
          return retryResponse;
        } else {
          return responseObject;
        }
      }
      if (options.triggerOnException === true) {
        await this.onException(responseObject);
      }
    } finally {
      console.log(
        method + ": " + uri + " (body: " + JSON.stringify(body, null, 2) + ") => " + JSON.stringify(responseObject, null, 2)
      );
    }
    return responseObject;
  }
  async sendGetRequest(token, uri) {
    const headers = {
      "Content-Type": "application/json",
      "Client-Version": this.clientVersion
    };
    if (token !== void 0 && token !== null) {
      headers.Authorization = `Bearer ${token}`;
    }
    return await fetch(uri, {
      method: "GET",
      headers
    });
  }
  async sendPostRequest(token, uri, body) {
    const headers = {
      "Content-Type": "application/json",
      "Client-Version": this.clientVersion
    };
    if (token !== void 0 && token !== null) {
      headers.Authorization = `Bearer ${token}`;
    }
    return await fetch(uri, {
      method: "POST",
      headers,
      body: JSON.stringify(body)
    });
  }
  async sendPatchRequest(token, uri, body) {
    const headers = {
      "Content-Type": "application/json",
      "Client-Version": this.clientVersion
    };
    if (token !== void 0 && token !== null) {
      headers.Authorization = `Bearer ${token}`;
    }
    return await fetch(uri, {
      method: "PATCH",
      headers,
      body: JSON.stringify(body)
    });
  }
  async sendDeleteRequest(token, uri) {
    const headers = {
      "Content-Type": "application/json",
      "Client-Version": this.clientVersion
    };
    if (token !== void 0 && token !== null) {
      headers.Authorization = `Bearer ${token}`;
    }
    return await fetch(uri, {
      method: "DELETE",
      headers
    });
  }
  connectToWebsocket({ url }) {
    const uri = this.wssBaseUrl + url;
    console.log("connecting to websocket", uri);
    const socket = io(uri, { transports: ["websocket"] });
    socket.on("connect", () => {
      console.log("connected to websocket", uri);
    });
    socket.on("connect_error", (error) => {
      console.error("Connection error:", error);
    });
    return socket;
  }
  disconnectFromWebsocket(socket) {
    socket.disconnect();
  }
};

// src/sessions/sessions.service.ts
var SessionService = class extends Service {
  constructor(config) {
    super(config);
  }
  async create(SessionCreateParams, SessionCreateOptions) {
    return await super.sendRequest(
      {
        method: "POST",
        url: "sessions",
        body: {
          locationId: SessionCreateParams.locationId,
          tariffId: SessionCreateParams.tariffId,
          autoRenew: SessionCreateParams.autoRenew,
          type: SessionCreateParams.sessionType,
          isOffSession: SessionCreateParams.isOffSession,
          userId: SessionCreateParams.userId
        },
        authenticationNotOptional: true
      },
      SessionCreateOptions
    );
  }
  async export(SessionExportParams, SessionExportOptions) {
    return await super.sendRequest(
      {
        method: "POST",
        url: "sessions/export",
        body: {
          propertyId: SessionExportParams.propertyId,
          outputType: SessionExportParams.outputType
        },
        authenticationNotOptional: true
      },
      SessionExportOptions
    );
  }
  async retrieve(SessionRetrieveParams, SessionRetrieveOptions) {
    const queryArray = [];
    if (SessionRetrieveOptions?.expand !== void 0) {
      queryArray.push("expand=" + SessionRetrieveOptions.expand.join(","));
    }
    return await super.sendRequest({
      method: "GET",
      url: "sessions/" + SessionRetrieveParams.sessionId + "?" + queryArray.join("&"),
      body: void 0,
      authenticationNotOptional: true
    });
  }
  async list(SessionListParams, SessionListOptions) {
    const queryArray = [];
    if (SessionListParams.userId !== void 0) {
      queryArray.push("userId=" + SessionListParams.userId);
    }
    if (SessionListParams.propertyId !== void 0) {
      queryArray.push("propertyId=" + SessionListParams.propertyId);
    }
    if (SessionListParams.statusArray !== void 0) {
      queryArray.push("statusArray=" + SessionListParams.statusArray.join(","));
    }
    if (SessionListParams.managementUserId !== void 0) {
      queryArray.push("managementUserId=" + SessionListParams.managementUserId);
    }
    if (SessionListParams.locationGroupId !== void 0) {
      queryArray.push("locationGroupId=" + SessionListParams.locationGroupId);
    }
    if (SessionListParams.locationId !== void 0) {
      queryArray.push("locationId=" + SessionListParams.locationId);
    }
    if (SessionListOptions?.limit !== void 0) {
      queryArray.push("limit=" + SessionListOptions.limit);
    }
    if (SessionListOptions?.skip !== void 0) {
      queryArray.push("skip=" + SessionListOptions.skip);
    }
    return await super.sendRequest(
      {
        method: "GET",
        url: "sessions?" + queryArray.join("&"),
        body: void 0,
        authenticationNotOptional: true
      },
      SessionListOptions
    );
  }
  async update(SessionUpdateParams, SessionUpdateOptions) {
    return await super.sendRequest(
      {
        method: "PATCH",
        url: "sessions/" + SessionUpdateParams.sessionId,
        body: {
          autoRenew: SessionUpdateParams?.autoRenew
        },
        authenticationNotOptional: true
      },
      SessionUpdateOptions
    );
  }
  async terminate(SessionTerminateParams, SessionTerminateOptions) {
    return await super.sendRequest(
      {
        method: "PATCH",
        url: "sessions/" + SessionTerminateParams.sessionId + "/terminate",
        body: {
          isOffSession: SessionTerminateParams.isOffSession
        },
        authenticationNotOptional: true
      },
      SessionTerminateOptions
    );
  }
  async attachLocation(SessionTerminateParams, SessionTerminateOptions) {
    return await super.sendRequest(
      {
        method: "PATCH",
        url: "sessions/" + SessionTerminateParams.sessionId + "/attachLocation",
        body: {
          locationId: SessionTerminateParams.locationId
        },
        authenticationNotOptional: true
      },
      SessionTerminateOptions
    );
  }
  async attachUser(SessionAttachUserParams, SessionAttachUserOptions) {
    return await super.sendRequest(
      {
        method: "POST",
        url: "sessions/" + SessionAttachUserParams.sessionId + "/user",
        body: {
          userId: SessionAttachUserParams.userId
        },
        authenticationNotOptional: true
      },
      SessionAttachUserOptions
    );
  }
  async detachUser(SessionDetachUserParams, SessionDetachUserOptions) {
    return await super.sendRequest(
      {
        method: "DELETE",
        url: "sessions/" + SessionDetachUserParams.sessionId + "/user",
        body: void 0,
        authenticationNotOptional: true
      },
      SessionDetachUserOptions
    );
  }
};

// src/links/links.service.ts
var LinkService = class extends Service {
  constructor(config) {
    super(config);
  }
  async create(LinkCreateParams, LinkCreateOptions) {
    return await super.sendRequest(
      {
        method: "POST",
        url: "links",
        body: {
          propertyId: LinkCreateParams.propertyId,
          name: LinkCreateParams.name,
          fiveLetterQr: LinkCreateParams.fiveLetterQr
        },
        authenticationNotOptional: true
      },
      LinkCreateOptions
    );
  }
  async retrieve(LinkRetrieveParams, LinkRetrieveOptions) {
    const queryArray = [];
    return await super.sendRequest(
      {
        method: "GET",
        url: "links/" + LinkRetrieveParams.linkId + "?" + queryArray.join("&"),
        body: void 0,
        authenticationNotOptional: false
      },
      LinkRetrieveOptions
    );
  }
  async list(LinkListParams, LinkListOptions) {
    const queryArray = [];
    if (LinkListParams.propertyId !== void 0) {
      queryArray.push("propertyId=" + LinkListParams.propertyId);
    }
    if (LinkListParams.fiveLetterQr !== void 0) {
      queryArray.push("fiveLetterQr=" + LinkListParams.fiveLetterQr);
    }
    return await super.sendRequest(
      {
        method: "GET",
        url: "links?" + queryArray.join("&"),
        body: void 0,
        authenticationNotOptional: false
      },
      LinkListOptions
    );
  }
  async delete(LinkDeleteParams, LinkDeleteOptions) {
    return await super.sendRequest(
      {
        method: "DELETE",
        url: "links/" + LinkDeleteParams.linkId,
        authenticationNotOptional: true,
        body: void 0
      },
      LinkDeleteOptions
    );
  }
};

// src/users/users.service.ts
var UsersService = class extends Service {
  constructor(config) {
    super(config);
  }
  async retrieve(UserRetrieveParams, UserRetrieveOptions) {
    const queryArray = [];
    return await super.sendRequest(
      {
        method: "GET",
        url: "users/" + UserRetrieveParams.userId + "?" + queryArray.join("&"),
        body: void 0,
        authenticationNotOptional: true
      },
      UserRetrieveOptions
    );
  }
  async exists(UserExistsParams, UserExistsOptions) {
    return await super.sendRequest(
      {
        method: "GET",
        url: "users/exists?email=" + UserExistsParams.email,
        body: void 0,
        authenticationNotOptional: false
      },
      UserExistsOptions
    );
  }
  async registerEmailPassword(UserRegisterEmailPasswordParams, UserRegisterEmailPasswordOptions) {
    return await super.sendRequest(
      {
        method: "POST",
        url: "auth/emailPassword/register",
        body: {
          email: UserRegisterEmailPasswordParams.email,
          password: UserRegisterEmailPasswordParams.password
        },
        authenticationNotOptional: false
      },
      UserRegisterEmailPasswordOptions
    );
  }
  async loginEmailPassword(UserLoginEmailPasswordParams, UserLoginEmailPasswordOptions) {
    return await super.sendRequest(
      {
        method: "POST",
        url: "auth/emailPassword/login",
        body: {
          email: UserLoginEmailPasswordParams.email,
          password: UserLoginEmailPasswordParams.password
        },
        authenticationNotOptional: false
      },
      UserLoginEmailPasswordOptions
    );
  }
  async paymentMethodTokens(UserPaymentMethodTokensParams, UserPaymentMethodTokensOptions) {
    return await super.sendRequest(
      {
        method: "GET",
        url: "users/" + UserPaymentMethodTokensParams.userId + "/paymentMethodTokens",
        body: void 0,
        authenticationNotOptional: true
      },
      UserPaymentMethodTokensOptions
    );
  }
  async refreshAccessToken(UserRefreshAccessTokenParams, UserRefreshAccessTokenOptions) {
    return await super.sendRequest(
      {
        method: "GET",
        url: "auth/refresh",
        body: void 0,
        authenticationNotOptional: true
      },
      {
        accessToken: UserRefreshAccessTokenParams?.refreshToken,
        ...UserRefreshAccessTokenOptions
      }
    );
  }
  async list(UserListParams, UserListOptions) {
    const queryArray = [];
    if (UserListParams.managementUserId !== void 0) {
      queryArray.push("managementUserId=" + UserListParams.managementUserId);
    }
    if (UserListParams.propertyId !== void 0) {
      queryArray.push("propertyId=" + UserListParams.propertyId);
    }
    return await super.sendRequest(
      {
        method: "GET",
        url: "users?" + queryArray.join("&"),
        body: void 0,
        authenticationNotOptional: true
      },
      UserListOptions
    );
  }
  async update(UserUpdateParams, UserUpdateOptions) {
    return await super.sendRequest(
      {
        method: "PATCH",
        url: "users/" + UserUpdateParams.userId,
        body: {
          name: UserUpdateParams?.name,
          licenseArray: UserUpdateParams?.licenseArray,
          platform: UserUpdateParams?.platform,
          languageCode: UserUpdateParams?.languageCode,
          appVersion: UserUpdateParams?.appVersion,
          billingAddress: UserUpdateParams?.billingAddress,
          vat: UserUpdateParams?.vat,
          acceptedTermIdArray: UserUpdateParams?.acceptedTermIdArray
        },
        authenticationNotOptional: true
      },
      UserUpdateOptions
    );
  }
  async memberCreate(UserInviteMemberParams, UserInviteMemberOptions) {
    return await super.sendRequest(
      {
        method: "POST",
        url: "users/" + UserInviteMemberParams.userId + "/members",
        body: {
          userId: UserInviteMemberParams?.userIdToInvite,
          email: UserInviteMemberParams?.email
        },
        authenticationNotOptional: true
      },
      UserInviteMemberOptions
    );
  }
  async memberDelete(UserRemoveMemberParams, UserRemoveMemberOptions) {
    return await super.sendRequest(
      {
        method: "DELETE",
        url: "users/" + UserRemoveMemberParams.managementUserId + "/members/" + UserRemoveMemberParams.memberUserId,
        body: void 0,
        authenticationNotOptional: true
      },
      UserRemoveMemberOptions
    );
  }
  async delete(UserDeleteParams, UserDeleteOptions) {
    return await super.sendRequest(
      {
        method: "DELETE",
        url: "users/" + UserDeleteParams.userId,
        authenticationNotOptional: true,
        body: void 0
      },
      UserDeleteOptions
    );
  }
};

// src/payments/payments.service.ts
var PaymentsService = class extends Service {
  constructor(config) {
    super(config);
  }
  async list(PaymentListParams, PaymentListOptions) {
    const queryArray = [];
    if (PaymentListParams.userId !== void 0) {
      queryArray.push("userId=" + PaymentListParams.userId);
    }
    if (PaymentListParams.propertyId !== void 0) {
      queryArray.push("propertyId=" + PaymentListParams.propertyId);
    }
    if (PaymentListParams.statusArray !== void 0) {
      queryArray.push("statusArray=" + PaymentListParams.statusArray.join(","));
    }
    if (PaymentListParams?.createdAt?.gte !== void 0) {
      queryArray.push("createdAt[gte]=" + PaymentListParams.createdAt.gte);
    }
    if (PaymentListParams?.createdAt?.lte !== void 0) {
      queryArray.push("createdAt[lte]=" + PaymentListParams.createdAt.lte);
    }
    if (PaymentListParams.limit !== void 0) {
      queryArray.push("limit=" + PaymentListParams.limit);
    }
    if (PaymentListParams.skip !== void 0) {
      queryArray.push("skip=" + PaymentListParams.skip);
    }
    return await super.sendRequest(
      {
        method: "GET",
        url: "payments?" + queryArray.join("&"),
        body: void 0,
        authenticationNotOptional: true
      },
      PaymentListOptions
    );
  }
  async retrieve(PaymentRetrieveParams, PaymentRetrieveOptions) {
    const queryArray = [];
    if (PaymentRetrieveOptions?.expand !== void 0) {
      queryArray.push("expand=" + PaymentRetrieveOptions.expand.join(","));
    }
    return await super.sendRequest({
      method: "GET",
      url: "payments/" + PaymentRetrieveParams.paymentId + "?" + queryArray.join("&"),
      body: void 0,
      authenticationNotOptional: true
    });
  }
  async tokens(PaymentTokensParams, PaymentTokensOptions) {
    return await super.sendRequest(
      {
        method: "GET",
        url: "payments/" + PaymentTokensParams.paymentId + "/tokens",
        body: void 0,
        authenticationNotOptional: true
      },
      PaymentTokensOptions
    );
  }
  async retrieveInvoiceUrl(PaymentRetrieveInvoiceUrlParams, PaymentRetrieveInvoiceUrlOptions) {
    const queryArray = [];
    return await super.sendRequest({
      method: "GET",
      url: "payments/" + PaymentRetrieveInvoiceUrlParams.paymentId + "/invoiceUrl",
      body: void 0,
      authenticationNotOptional: true
    });
  }
};

// src/properties/properties.service.ts
var PropertiesService = class extends Service {
  constructor(config) {
    super(config);
  }
  async create(PropertyCreateParams, PropertyCreateOptions) {
    const queryArray = [];
    return await super.sendRequest({
      method: "POST",
      url: "properties",
      body: {
        userId: PropertyCreateParams.userId,
        name: PropertyCreateParams.name,
        type: PropertyCreateParams.type
      },
      authenticationNotOptional: true
    });
  }
  async retrieve(PropertyRetrieveParams, PropertyRetrieveOptions) {
    const queryArray = [];
    return await super.sendRequest({
      method: "GET",
      url: "properties/" + PropertyRetrieveParams.propertyId + "?" + queryArray.join("&"),
      body: void 0,
      authenticationNotOptional: false
    });
  }
  async list(PropertyListParams, PropertyListOptions) {
    const queryArray = [];
    return await super.sendRequest(
      {
        method: "GET",
        url: "properties?" + queryArray.join("&"),
        body: void 0,
        authenticationNotOptional: false
      },
      PropertyListOptions
    );
  }
  async update(PropertyUpdateParams, PropertyUpdateOptions) {
    return await super.sendRequest(
      {
        method: "PATCH",
        url: "properties/" + PropertyUpdateParams.propertyId,
        body: {
          name: PropertyUpdateParams.name,
          legalName: PropertyUpdateParams.legalName,
          billingAddress: PropertyUpdateParams.billingAddress,
          email: PropertyUpdateParams.email,
          website: PropertyUpdateParams.website,
          phone: PropertyUpdateParams.phone,
          faqUrl: PropertyUpdateParams.faqUrl,
          colorScheme: PropertyUpdateParams.colorScheme,
          contactUrl: PropertyUpdateParams.contactUrl
        },
        authenticationNotOptional: true
      },
      PropertyUpdateOptions
    );
  }
  async stripeAccountUrl(PropertyStripeAccountUrlParams, PropertyStripeAccountUrlOptions) {
    return await super.sendRequest(
      {
        method: "GET",
        url: "properties/" + PropertyStripeAccountUrlParams.propertyId + "/stripeAccountUrl",
        body: void 0,
        authenticationNotOptional: true
      },
      PropertyStripeAccountUrlOptions
    );
  }
};

// src/points/points.service.ts
var PointsService = class extends Service {
  constructor(config) {
    super(config);
  }
  async map(PointListParams, PointListOptions) {
    const queryArray = [
      "modalityArray=" + PointListParams.modalityArray.join(","),
      "categoryArray=" + PointListParams.categoryArray.join(","),
      "sectorArray=" + PointListParams.sectorArray.join(","),
      "longitudeTopLeft=" + PointListParams.longitudeTopLeft,
      "latitudeTopLeft=" + PointListParams.latitudeTopLeft,
      "longitudeBottomRight=" + PointListParams.longitudeBottomRight,
      "latitudeBottomRight=" + PointListParams.latitudeBottomRight
    ];
    return await super.sendRequest(
      {
        method: "GET",
        url: "points/map?" + queryArray.join("&"),
        body: void 0,
        authenticationNotOptional: false
      },
      PointListOptions
    );
  }
  async list(PointListParams, PointListOptions) {
    const queryArray = [];
    if (PointListParams?.propertyId !== void 0) {
      queryArray.push("propertyId=" + PointListParams.propertyId);
    }
    return await super.sendRequest(
      {
        method: "GET",
        url: "points?" + queryArray.join("&"),
        body: void 0,
        authenticationNotOptional: false
      },
      PointListOptions
    );
  }
  toRadians(degrees) {
    return degrees * Math.PI / 180;
  }
  calculateDistance(lat1, lon1, lat2, lon2) {
    var R = 6371e3;
    var \u03C61 = this.toRadians(lat1);
    var \u03C62 = this.toRadians(lat2);
    var \u0394\u03C6 = this.toRadians(lat2 - lat1);
    var \u0394\u03BB = this.toRadians(lon2 - lon1);
    var a = Math.sin(\u0394\u03C6 / 2) * Math.sin(\u0394\u03C6 / 2) + Math.cos(\u03C61) * Math.cos(\u03C62) * Math.sin(\u0394\u03BB / 2) * Math.sin(\u0394\u03BB / 2);
    var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }
  calculateAltitudeForTopDownView(topLeftLat, topLeftLon, bottomRightLat, bottomRightLon) {
    var diagonalDistance = this.calculateDistance(
      topLeftLat,
      topLeftLon,
      bottomRightLat,
      bottomRightLon
    );
    const diagonalDistanceHalf = Math.round(diagonalDistance / 2);
    if (diagonalDistanceHalf < 0) {
      return 0;
    }
    if (diagonalDistanceHalf > 1e6) {
      return 1e6;
    }
    return diagonalDistanceHalf;
  }
};

// src/devices/devices.service.ts
var DevicesService = class extends Service {
  constructor(config) {
    super(config);
  }
  async create(DeviceCreateParams, DeviceCreateOptions) {
    return await super.sendRequest(
      {
        method: "POST",
        url: "devices",
        body: {
          propertyId: DeviceCreateParams.propertyId,
          deviceTemplateId: DeviceCreateParams.deviceTemplateId,
          name: DeviceCreateParams.name
        },
        authenticationNotOptional: true
      },
      DeviceCreateOptions
    );
  }
  async retrieve(DeviceRetrieveParams, DeviceRetrieveOptions) {
    const queryArray = [];
    if (DeviceRetrieveParams?.source !== void 0) {
      queryArray.push("source=" + DeviceRetrieveParams.source);
    }
    if (DeviceRetrieveOptions?.expand !== void 0) {
      queryArray.push("expand=" + DeviceRetrieveOptions.expand.join(","));
    }
    return await super.sendRequest(
      {
        method: "GET",
        url: "devices/" + DeviceRetrieveParams.deviceId + "?" + queryArray.join("&"),
        body: void 0,
        authenticationNotOptional: false
      },
      DeviceRetrieveOptions
    );
  }
  async list(DeviceListParams, DeviceListOptions) {
    const queryArray = [];
    if (DeviceListParams?.statusArray !== void 0) {
      queryArray.push("statusArray=" + DeviceListParams.statusArray.join(","));
    }
    if (DeviceListParams?.propertyId !== void 0) {
      queryArray.push("propertyId=" + DeviceListParams.propertyId);
    }
    if (DeviceListParams?.deviceTemplateId !== void 0) {
      queryArray.push("deviceTemplateId=" + DeviceListParams.deviceTemplateId);
    }
    if (DeviceListOptions?.limit !== void 0) {
      queryArray.push("limit=" + DeviceListOptions.limit);
    }
    if (DeviceListOptions?.skip !== void 0) {
      queryArray.push("skip=" + DeviceListOptions.skip);
    }
    return await super.sendRequest(
      {
        method: "GET",
        url: "devices?" + queryArray.join("&"),
        body: void 0,
        authenticationNotOptional: false
      },
      DeviceListOptions
    );
  }
  listen(DeviceRealtimeParams, DeviceRealtimeOptions) {
    const socket = super.connectToWebsocket({
      url: "devices/" + DeviceRealtimeParams.deviceId + "/websocket"
    });
    const onUpdated = (onUpdatedCallback) => {
      socket.on("updated", (message) => {
        onUpdatedCallback(message);
      });
    };
    return {
      onUpdated,
      close: () => {
        console.log("closing websocket connection");
        socket.close();
      }
    };
  }
  async message(DeviceMessageParams, DeviceMessageOptions) {
    return await super.sendRequest(
      {
        method: "POST",
        url: "devices?" + DeviceMessageParams.deviceId + "/message",
        body: {
          message: DeviceMessageParams.message
        },
        authenticationNotOptional: true
      },
      DeviceMessageOptions
    );
  }
  async parameterUpdate(DeviceParameterParams, DeviceParameterOptions) {
    return await super.sendRequest(
      {
        method: "PATCH",
        url: "devices/" + DeviceParameterParams.deviceId + "/parameter/" + DeviceParameterParams.parameterName,
        body: {
          value: DeviceParameterParams.value
        },
        authenticationNotOptional: true
      },
      DeviceParameterOptions
    );
  }
  async commandExecute(DeviceCommandExecuteParams, DeviceCommandExecuteOptions) {
    return await super.sendRequest(
      {
        method: "POST",
        url: "devices/" + DeviceCommandExecuteParams.deviceId + "/command/" + DeviceCommandExecuteParams.commandName,
        body: {},
        authenticationNotOptional: true
      },
      DeviceCommandExecuteOptions
    );
  }
  async delete(DeviceDeleteParams, DeviceDeleteOptions) {
    return await super.sendRequest(
      {
        method: "DELETE",
        url: "devices/" + DeviceDeleteParams.deviceId,
        authenticationNotOptional: true,
        body: void 0
      },
      DeviceDeleteOptions
    );
  }
};

// src/deviceTemplates/deviceTemplates.service.ts
var DeviceTemplatesService = class extends Service {
  constructor(config) {
    super(config);
  }
  async create(DeviceTemplateCreateParams, DeviceTemplateCreateOptions) {
    return await super.sendRequest(
      {
        method: "POST",
        url: "deviceTemplates",
        body: {
          propertyId: DeviceTemplateCreateParams.propertyId,
          productId: DeviceTemplateCreateParams.productId,
          name: DeviceTemplateCreateParams.name
        },
        authenticationNotOptional: true
      },
      DeviceTemplateCreateOptions
    );
  }
  async retrieve(DeviceTemplateRetrieveParams, DeviceTemplateRetrieveOptions) {
    const queryArray = [];
    if (DeviceTemplateRetrieveParams?.source !== void 0) {
      queryArray.push("source=" + DeviceTemplateRetrieveParams.source);
    }
    return await super.sendRequest(
      {
        method: "GET",
        url: "deviceTemplates/" + DeviceTemplateRetrieveParams.deviceTemplateId + "?" + queryArray.join("&"),
        body: void 0,
        authenticationNotOptional: false
      },
      DeviceTemplateRetrieveOptions
    );
  }
  async list(DeviceTemplateListParams, DeviceTemplateListOptions) {
    const queryArray = [];
    if (DeviceTemplateListParams?.propertyId !== void 0) {
      queryArray.push("propertyId=" + DeviceTemplateListParams.propertyId);
    }
    return await super.sendRequest(
      {
        method: "GET",
        url: "deviceTemplates?" + queryArray.join("&"),
        body: void 0,
        authenticationNotOptional: false
      },
      DeviceTemplateListOptions
    );
  }
  async delete(DeviceTemplateDeleteParams, DeviceTemplateDeleteOptions) {
    return await super.sendRequest(
      {
        method: "DELETE",
        url: "deviceTemplates/" + DeviceTemplateDeleteParams.deviceTemplateId,
        authenticationNotOptional: true,
        body: void 0
      },
      DeviceTemplateDeleteOptions
    );
  }
};

// src/locations/locations.service.ts
var LocationsService = class extends Service {
  constructor(config) {
    super(config);
  }
  async create(LocationCreateParams, LocationCreateOptions) {
    return await super.sendRequest(
      {
        method: "POST",
        url: "locations",
        body: {
          propertyId: LocationCreateParams.propertyId,
          name: LocationCreateParams.name,
          type: LocationCreateParams.type
        },
        authenticationNotOptional: true
      },
      LocationCreateOptions
    );
  }
  async retrieve(LocationRetrieveParams, LocationRetrieveOptions) {
    const queryArray = [];
    if (LocationRetrieveOptions?.expand !== void 0) {
      queryArray.push("expand=" + LocationRetrieveOptions.expand.join(","));
    }
    return await super.sendRequest(
      {
        method: "GET",
        url: "locations/" + LocationRetrieveParams.locationId + "?" + queryArray.join("&"),
        body: void 0,
        authenticationNotOptional: false
      },
      LocationRetrieveOptions
    );
  }
  async list(LocationListParams, LocationListOptions) {
    const queryArray = [];
    if (LocationListParams?.rentableDeviceGroupLocationId !== void 0) {
      const rentableDeviceGroupLocationId = LocationListParams.rentableDeviceGroupLocationId === null ? "null" : LocationListParams.rentableDeviceGroupLocationId;
      queryArray.push(
        "rentableDeviceGroupLocationId=" + rentableDeviceGroupLocationId
      );
    }
    if (LocationListParams?.propertyId !== void 0) {
      queryArray.push("propertyId=" + LocationListParams.propertyId);
    }
    return await super.sendRequest(
      {
        method: "GET",
        url: "locations?" + queryArray.join("&"),
        body: void 0,
        authenticationNotOptional: false
      },
      LocationListOptions
    );
  }
  async update(LocationUpdateParams, LocationUpdateOptions) {
    return await super.sendRequest(
      {
        method: "PATCH",
        url: "locations/" + LocationUpdateParams.locationId,
        body: {
          name: LocationUpdateParams.name,
          address: LocationUpdateParams.address,
          deviceIdArray: LocationUpdateParams.deviceIdArray,
          deviceId: LocationUpdateParams.deviceId,
          maximumConcurrentSessions: LocationUpdateParams.maximumConcurrentSessions,
          surveyEnabled: LocationUpdateParams.surveyEnabled,
          accountingAreaId: LocationUpdateParams.accountingAreaId,
          purposeArray: LocationUpdateParams.purposeArray,
          circumstanceArray: LocationUpdateParams.circumstanceArray,
          rentOfferArray: LocationUpdateParams.rentOfferArray,
          reservationOfferArray: LocationUpdateParams.reservationOfferArray
        },
        authenticationNotOptional: true
      },
      LocationUpdateOptions
    );
  }
  async delete(LocationDeleteParams, LocationDeleteOptions) {
    return await super.sendRequest(
      {
        method: "DELETE",
        url: "locations/" + LocationDeleteParams.locationId,
        authenticationNotOptional: true,
        body: void 0
      },
      LocationDeleteOptions
    );
  }
};

// src/terms/terms.service.ts
var TermsService = class extends Service {
  constructor(config) {
    super(config);
  }
  async create(TermCreateParams, TermCreateOptions) {
    return await super.sendRequest(
      {
        method: "POST",
        url: "terms",
        body: {
          propertyId: TermCreateParams.propertyId,
          name: TermCreateParams.name
        },
        authenticationNotOptional: true
      },
      TermCreateOptions
    );
  }
  async retrieve(TermRetrieveParams, TermRetrieveOptions) {
    const queryArray = [];
    if (TermRetrieveOptions?.expand !== void 0) {
      queryArray.push("expand=" + TermRetrieveOptions.expand.join(","));
    }
    return await super.sendRequest(
      {
        method: "GET",
        url: "terms/" + TermRetrieveParams.termId + "?" + queryArray.join("&"),
        body: void 0,
        authenticationNotOptional: false
      },
      TermRetrieveOptions
    );
  }
  async list(TermListParams, TermListOptions) {
    const queryArray = [];
    if (TermListParams?.propertyId !== void 0) {
      queryArray.push("propertyId=" + TermListParams.propertyId);
    }
    return await super.sendRequest(
      {
        method: "GET",
        url: "terms?" + queryArray.join("&"),
        body: void 0,
        authenticationNotOptional: false
      },
      TermListOptions
    );
  }
  async accept(TermAcceptParams, TermAcceptOptions) {
    return await super.sendRequest(
      {
        method: "PATCH",
        url: "terms/" + TermAcceptParams.termId + "/accept",
        body: {
          userId: TermAcceptParams.userId
        },
        authenticationNotOptional: true
      },
      TermAcceptOptions
    );
  }
  async delete(TermDeleteParams, TermDeleteOptions) {
    return await super.sendRequest(
      {
        method: "DELETE",
        url: "terms/" + TermDeleteParams.termId,
        authenticationNotOptional: true,
        body: void 0
      },
      TermDeleteOptions
    );
  }
};

// src/tariffs/tariffs.service.ts
var TariffsService = class extends Service {
  constructor(config) {
    super(config);
  }
  async create(TariffCreateParams, TariffCreateOptions) {
    return await super.sendRequest(
      {
        method: "POST",
        url: "tariffs",
        body: {
          propertyId: TariffCreateParams.propertyId,
          duration: TariffCreateParams.duration,
          currencyCode: TariffCreateParams.currencyCode,
          amount: TariffCreateParams.amount,
          continue: TariffCreateParams.continue,
          name: TariffCreateParams.name
        },
        authenticationNotOptional: true
      },
      TariffCreateOptions
    );
  }
  async retrieve(TariffRetrieveParams, TariffRetrieveOptions) {
    const queryArray = [];
    if (TariffRetrieveOptions?.expand !== void 0) {
      queryArray.push("expand=" + TariffRetrieveOptions.expand.join(","));
    }
    return await super.sendRequest(
      {
        method: "GET",
        url: "tariffs/" + TariffRetrieveParams.tariffId + "?" + queryArray.join("&"),
        body: void 0,
        authenticationNotOptional: false
      },
      TariffRetrieveOptions
    );
  }
  async list(TariffListParams, TariffListOptions) {
    const queryArray = [];
    if (TariffListParams?.propertyId !== void 0) {
      queryArray.push("propertyId=" + TariffListParams.propertyId);
    }
    return await super.sendRequest(
      {
        method: "GET",
        url: "tariffs?" + queryArray.join("&"),
        body: void 0,
        authenticationNotOptional: false
      },
      TariffListOptions
    );
  }
  async update(TariffUpdateParams, TariffUpdateOptions) {
    return await super.sendRequest(
      {
        method: "PATCH",
        url: "tariffs/" + TariffUpdateParams.tariffId,
        body: {
          reminderEnabled: TariffUpdateParams.reminderEnabled,
          reminderPercentage: TariffUpdateParams.reminderPercentage,
          currencyCode: TariffUpdateParams.currencyCode,
          amount: TariffUpdateParams.amount,
          continue: TariffUpdateParams.continue,
          interval: TariffUpdateParams.interval,
          duration: TariffUpdateParams.duration,
          autoRenewMode: TariffUpdateParams.autoRenewMode,
          roundToMidnight: TariffUpdateParams.roundToMidnight,
          autoRenewManualEnabled: TariffUpdateParams.autoRenewManualEnabled,
          manualTerminationEnabled: TariffUpdateParams.manualTerminationEnabled
        },
        authenticationNotOptional: true
      },
      TariffUpdateOptions
    );
  }
  /**
   * Checks if the tariff is free
   */
  isFree(tariff) {
    if (tariff.amount.every((amount) => amount === 0) === true && tariff.continue === 0) {
      return true;
    }
    return false;
  }
  /**
   * Returns the amount of seconds the tariff needs to be active before the amount will not change anymore
   */
  getAmountFinalizationDuration(tariff) {
    if (this.isFree(tariff) === true) {
      return 0;
    }
    if (tariff.interval === 0) {
      return 0;
    }
    if (tariff.continue === 0) {
      return tariff.interval * tariff.amount.length;
    } else {
      return tariff.duration;
    }
  }
  /**
   * Calculates the amount for a give rent time in seconds
   */
  calculateAmount(tariff, rentTimeSeconds) {
    if (this.isFree(tariff) === true) {
      return 0;
    }
    if (tariff.interval === 0) {
      return tariff.amount[0];
    }
    if (rentTimeSeconds > tariff.duration) {
      console.warn("rentTimeS is greater than duration");
      rentTimeSeconds = tariff.duration;
    }
    let sum = 0;
    const startedIntervals = Math.ceil(rentTimeSeconds / tariff.interval);
    for (let i = 0; i < startedIntervals; i += 1) {
      if (i < tariff.amount.length) {
        sum += tariff.amount[i];
      } else {
        sum += tariff.continue;
      }
    }
    let serviceFee = Math.round(sum * tariff.serviceFeePercentage / 100);
    if (serviceFee < tariff.serviceFeeMin) {
      serviceFee = tariff.serviceFeeMin;
    } else if (serviceFee > tariff.serviceFeeMax) {
      serviceFee = tariff.serviceFeeMax;
    }
    return sum + serviceFee;
  }
  calculateMaximumAmount(tariff) {
    return this.calculateAmount(tariff, tariff.duration);
  }
  /**
   * Returns a date object containing the timestamp that a session would have to end at if it was created right now
   */
  getEndDate(tariff) {
    if (tariff.roundToMidnight === false) {
      return new Date(Date.now() + tariff.duration * 1e3);
    }
    const end = new Date(Date.now() + tariff.duration * 1e3);
    end.setHours(23, 59, 59, 999);
    return end;
  }
  async delete(TariffDeleteParams, TariffDeleteOptions) {
    return await super.sendRequest(
      {
        method: "DELETE",
        url: "tariffs/" + TariffDeleteParams.tariffId,
        authenticationNotOptional: true,
        body: void 0
      },
      TariffDeleteOptions
    );
  }
};

// src/products/products.service.ts
var ProductService = class extends Service {
  constructor(config) {
    super(config);
  }
  async create(ProductCreateParams, ProductCreateOptions) {
    return await super.sendRequest(
      {
        method: "POST",
        url: "products",
        body: {
          propertyId: ProductCreateParams.propertyId,
          name: ProductCreateParams.name
        },
        authenticationNotOptional: true
      },
      ProductCreateOptions
    );
  }
  async list(ProductListParams, ProductListOptions) {
    const queryArray = [];
    if (ProductListParams.categoryArray !== void 0) {
      queryArray.push(
        "categoryArray=" + ProductListParams.categoryArray.join(",")
      );
    }
    if (ProductListParams.modalityArray !== void 0) {
      queryArray.push(
        "modalityArray=" + ProductListParams.modalityArray.join(",")
      );
    }
    if (ProductListParams.sectorArray !== void 0) {
      queryArray.push("sectorArray=" + ProductListParams.sectorArray.join(","));
    }
    if (ProductListParams.technologyArray !== void 0) {
      queryArray.push(
        "technologyArray=" + ProductListParams.technologyArray.join(",")
      );
    }
    if (ProductListParams?.propertyId !== void 0) {
      queryArray.push("propertyId=" + ProductListParams.propertyId);
    }
    return await super.sendRequest(
      {
        method: "GET",
        url: "products?" + queryArray.join("&"),
        body: void 0,
        authenticationNotOptional: false
      },
      ProductListOptions
    );
  }
  async retrieve(params, options) {
    const queryArray = [];
    if (options?.expand !== void 0) {
      queryArray.push("expand=" + options.expand.join(","));
    }
    return await super.sendRequest(
      {
        method: "GET",
        url: "products/" + params.productId + "?" + queryArray.join("&"),
        body: void 0,
        authenticationNotOptional: false
      },
      options
    );
  }
  async delete(ProductDeleteParams, ProductDeleteOptions) {
    return await super.sendRequest(
      {
        method: "DELETE",
        url: "products/" + ProductDeleteParams.productId,
        authenticationNotOptional: true,
        body: void 0
      },
      ProductDeleteOptions
    );
  }
};

// src/settings/settings.service.ts
var SettingsService = class extends Service {
  constructor(config) {
    super(config);
  }
  async retrieve() {
    return await super.sendRequest({
      method: "GET",
      url: "sessions/",
      body: void 0,
      authenticationNotOptional: false
    });
  }
};

// src/accountingAreas/accountingAreas.service.ts
var AccountingAreasService = class extends Service {
  constructor(config) {
    super(config);
  }
  async create(AccountingAreaCreateParams, AccountingAreaCreateOptions) {
    return await super.sendRequest(
      {
        method: "POST",
        url: "accountingAreas",
        body: {
          propertyId: AccountingAreaCreateParams.propertyId,
          name: AccountingAreaCreateParams.name
        },
        authenticationNotOptional: true
      },
      AccountingAreaCreateOptions
    );
  }
  async retrieve(AccountingAreaRetrieveParams, AccountingAreaRetrieveOptions) {
    const queryArray = [];
    if (AccountingAreaRetrieveOptions?.expand !== void 0) {
      queryArray.push(
        "expand=" + AccountingAreaRetrieveOptions.expand.join(",")
      );
    }
    return await super.sendRequest(
      {
        method: "GET",
        url: "accountingAreas/" + AccountingAreaRetrieveParams.accountingAreaId + "?" + queryArray.join("&"),
        body: void 0,
        authenticationNotOptional: false
      },
      AccountingAreaRetrieveOptions
    );
  }
  async list(AccountingAreaListParams, AccountingAreaListOptions) {
    const queryArray = [];
    if (AccountingAreaListParams?.propertyId !== void 0) {
      queryArray.push("propertyId=" + AccountingAreaListParams.propertyId);
    }
    return await super.sendRequest(
      {
        method: "GET",
        url: "accountingAreas?" + queryArray.join("&"),
        body: void 0,
        authenticationNotOptional: false
      },
      AccountingAreaListOptions
    );
  }
  async delete(AccountingAreaDeleteParams, AccountingAreaDeleteOptions) {
    return await super.sendRequest(
      {
        method: "DELETE",
        url: "accountingAreas/" + AccountingAreaDeleteParams.accountingAreaId,
        authenticationNotOptional: true,
        body: void 0
      },
      AccountingAreaDeleteOptions
    );
  }
};

// src/connectors/connectors.service.ts
var ConnectorsService = class extends Service {
  constructor(config) {
    super(config);
  }
  async create(ConnectorCreateParams, ConnectorCreateOptions) {
    return await super.sendRequest(
      {
        method: "POST",
        url: "connectors",
        body: {
          propertyId: ConnectorCreateParams.propertyId,
          name: ConnectorCreateParams.name,
          username: ConnectorCreateParams.username,
          password: ConnectorCreateParams.password,
          clientId: ConnectorCreateParams.clientId,
          host: ConnectorCreateParams.host,
          port: ConnectorCreateParams.port,
          mqttRetain: ConnectorCreateParams.mqttRetain,
          mqttQos: ConnectorCreateParams.mqttQos,
          description: ConnectorCreateParams.description,
          simId: ConnectorCreateParams.simId,
          connectionMode: ConnectorCreateParams.connectionMode,
          type: ConnectorCreateParams.type
        },
        authenticationNotOptional: true
      },
      ConnectorCreateOptions
    );
  }
  async retrieve(ConnectorRetrieveParams, ConnectorRetrieveOptions) {
    const queryArray = [];
    if (ConnectorRetrieveOptions?.expand !== void 0) {
      queryArray.push("expand=" + ConnectorRetrieveOptions.expand.join(","));
    }
    return await super.sendRequest(
      {
        method: "GET",
        url: "connectors/" + ConnectorRetrieveParams.connectorId + "?" + queryArray.join("&"),
        body: void 0,
        authenticationNotOptional: false
      },
      ConnectorRetrieveOptions
    );
  }
  async list(ConnectorListParams, ConnectorListOptions) {
    const queryArray = [];
    if (ConnectorListParams?.propertyId !== void 0) {
      queryArray.push("propertyId=" + ConnectorListParams.propertyId);
    }
    return await super.sendRequest(
      {
        method: "GET",
        url: "connectors?" + queryArray.join("&"),
        body: void 0,
        authenticationNotOptional: false
      },
      ConnectorListOptions
    );
  }
  async delete(ConnectorDeleteParams, ConnectorDeleteOptions) {
    return await super.sendRequest(
      {
        method: "DELETE",
        url: "connectors/" + ConnectorDeleteParams.connectorId,
        authenticationNotOptional: true,
        body: void 0
      },
      ConnectorDeleteOptions
    );
  }
};

// src/payouts/payouts.service.ts
var PayoutsService = class extends Service {
  constructor(config) {
    super(config);
  }
  async retrieve(PayoutRetrieveParams, PayoutRetrieveOptions) {
    const queryArray = [];
    if (PayoutRetrieveOptions?.expand !== void 0) {
      queryArray.push("expand=" + PayoutRetrieveOptions.expand.join(","));
    }
    return await super.sendRequest(
      {
        method: "GET",
        url: "payouts/" + PayoutRetrieveParams.payoutId + "?" + queryArray.join("&"),
        body: void 0,
        authenticationNotOptional: false
      },
      PayoutRetrieveOptions
    );
  }
  async list(PayoutListParams, PayoutListOptions) {
    const queryArray = [];
    if (PayoutListParams?.propertyId !== void 0) {
      queryArray.push("propertyId=" + PayoutListParams.propertyId);
    }
    return await super.sendRequest(
      {
        method: "GET",
        url: "payouts?" + queryArray.join("&"),
        body: void 0,
        authenticationNotOptional: false
      },
      PayoutListOptions
    );
  }
};

// src/connectorMessages/connectorMessages.service.ts
var ConnectorMessagesService = class extends Service {
  constructor(config) {
    super(config);
  }
  async retrieve(ConnectorMessageRetrieveParams, ConnectorMessageRetrieveOptions) {
    const queryArray = [];
    return await super.sendRequest(
      {
        method: "GET",
        url: "connectorMessages/" + ConnectorMessageRetrieveParams.connectorMessageId + "?" + queryArray.join("&"),
        body: void 0,
        authenticationNotOptional: true
      },
      ConnectorMessageRetrieveOptions
    );
  }
  async list(ConnectorMessageListParams, ConnectorMessageListOptions) {
    const queryArray = [];
    if (ConnectorMessageListParams?.propertyId !== void 0) {
      queryArray.push("propertyId=" + ConnectorMessageListParams.propertyId);
    }
    if (ConnectorMessageListParams?.connectorId !== void 0) {
      queryArray.push("connectorId=" + ConnectorMessageListParams.connectorId);
    }
    if (ConnectorMessageListOptions?.skip !== void 0) {
      queryArray.push("skip=" + ConnectorMessageListOptions.skip);
    }
    if (ConnectorMessageListOptions?.limit !== void 0) {
      queryArray.push("limit=" + ConnectorMessageListOptions.limit);
    }
    return await super.sendRequest(
      {
        method: "GET",
        url: "connectorMessages?" + queryArray.join("&"),
        body: void 0,
        authenticationNotOptional: true
      },
      ConnectorMessageListOptions
    );
  }
};

// src/sims/sims.service.ts
var SimsService = class extends Service {
  constructor(config) {
    super(config);
  }
  async create(SimCreateParams, SimCreateOptions) {
    return await super.sendRequest(
      {
        method: "POST",
        url: "sims",
        body: {
          propertyId: SimCreateParams.propertyId,
          iccid: SimCreateParams.iccid,
          name: SimCreateParams.name
        },
        authenticationNotOptional: true
      },
      SimCreateOptions
    );
  }
  async retrieve(SimRetrieveParams, SimRetrieveOptions) {
    return await super.sendRequest(
      {
        method: "GET",
        url: "sims/" + SimRetrieveParams.simId,
        body: void 0,
        authenticationNotOptional: false
      },
      SimRetrieveOptions
    );
  }
  async list(SimListParams, SimListOptions) {
    const queryArray = [];
    if (SimListParams?.propertyId !== void 0) {
      queryArray.push("propertyId=" + SimListParams.propertyId);
    }
    return await super.sendRequest(
      {
        method: "GET",
        url: "sims?" + queryArray.join("&"),
        body: void 0,
        authenticationNotOptional: false
      },
      SimListOptions
    );
  }
  async updateFromProvider(SimUpdateFromProviderParams, SimUpdateFromProviderOptions) {
    return await super.sendRequest(
      {
        method: "PATCH",
        url: "sims/" + SimUpdateFromProviderParams.simId + "/updateFromProvider",
        body: void 0,
        authenticationNotOptional: false
      },
      SimUpdateFromProviderOptions
    );
  }
  async delete(SimDeleteParams, SimDeleteOptions) {
    return await super.sendRequest(
      {
        method: "DELETE",
        url: "sims/" + SimDeleteParams.simId,
        authenticationNotOptional: true,
        body: void 0
      },
      SimDeleteOptions
    );
  }
};

// src/licenseTemplates/licenseTemplates.service.ts
var LicenseTemplatesService = class extends Service {
  constructor(config) {
    super(config);
  }
  async create(LicenseTemplateCreateParams, LicenseTemplateCreateOptions) {
    return await super.sendRequest(
      {
        method: "POST",
        url: "licenseTemplates",
        body: {
          propertyId: LicenseTemplateCreateParams.propertyId,
          type: LicenseTemplateCreateParams.type,
          regex: LicenseTemplateCreateParams.regex,
          name: LicenseTemplateCreateParams.name
        },
        authenticationNotOptional: true
      },
      LicenseTemplateCreateOptions
    );
  }
  async list(LicenseTemplateListParams, LicenseTemplateListOptions) {
    const queryArray = [];
    if (LicenseTemplateListParams.propertyId !== void 0) {
      queryArray.push("propertyId=" + LicenseTemplateListParams.propertyId);
    }
    if (LicenseTemplateListParams.limit !== void 0) {
      queryArray.push("limit=" + LicenseTemplateListParams.limit);
    }
    if (LicenseTemplateListParams.skip !== void 0) {
      queryArray.push("skip=" + LicenseTemplateListParams.skip);
    }
    return await super.sendRequest(
      {
        method: "GET",
        url: "licenseTemplates?" + queryArray.join("&"),
        body: void 0,
        authenticationNotOptional: true
      },
      LicenseTemplateListOptions
    );
  }
  async retrieve(LicenseTemplateRetrieveParams, LicenseTemplateRetrieveOptions) {
    const queryArray = [];
    if (LicenseTemplateRetrieveOptions?.expand !== void 0) {
      queryArray.push(
        "expand=" + LicenseTemplateRetrieveOptions.expand.join(",")
      );
    }
    return await super.sendRequest({
      method: "GET",
      url: "licenseTemplates/" + LicenseTemplateRetrieveParams.licenseTemplateId + "?" + queryArray.join("&"),
      body: void 0,
      authenticationNotOptional: true
    });
  }
  async regexValidate(LicenseTemplateRegexValidateParams, LicenseTemplateRegexValidateOptions) {
    return await super.sendRequest(
      {
        method: "POST",
        url: "licenseTemplates/" + LicenseTemplateRegexValidateParams.licenseTemplateId + "/regex/validate",
        body: {
          userId: LicenseTemplateRegexValidateParams.userId,
          text: LicenseTemplateRegexValidateParams.text,
          licenseTemplateId: LicenseTemplateRegexValidateParams.licenseTemplateId
        },
        authenticationNotOptional: true
      },
      LicenseTemplateRegexValidateOptions
    );
  }
  async delete(LicenseTemplateDeleteParams, LicenseTemplateDeleteOptions) {
    return await super.sendRequest(
      {
        method: "DELETE",
        url: "licenseTemplates/" + LicenseTemplateDeleteParams.licenseTemplateId,
        authenticationNotOptional: true,
        body: void 0
      },
      LicenseTemplateDeleteOptions
    );
  }
};

// src/articles/articles.service.ts
var ArticlesService = class extends Service {
  constructor(config) {
    super(config);
  }
  async create(ArticleCreateParams, ArticleCreateOptions) {
    return await super.sendRequest(
      {
        method: "POST",
        url: "articles",
        body: {
          propertyId: ArticleCreateParams.propertyId
        },
        authenticationNotOptional: true
      },
      ArticleCreateOptions
    );
  }
  async list(ArticleListParams, ArticleListOptions) {
    const queryArray = [];
    if (ArticleListOptions?.limit !== void 0) {
      queryArray.push("limit=" + ArticleListOptions.limit);
    }
    if (ArticleListParams?.propertyId !== void 0) {
      queryArray.push("propertyId=" + ArticleListParams.propertyId);
    }
    if (ArticleListParams?.statusArray !== void 0) {
      queryArray.push("statusArray=" + ArticleListParams.statusArray.join(","));
    }
    if (ArticleListOptions?.skip !== void 0) {
      queryArray.push("skip=" + ArticleListOptions.skip);
    }
    return await super.sendRequest(
      {
        method: "GET",
        url: "articles?" + queryArray.join("&"),
        body: void 0,
        authenticationNotOptional: false
      },
      ArticleListOptions
    );
  }
  async retrieve(ArticleRetrieveParams, ArticleRetrieveOptions) {
    const queryArray = [];
    if (ArticleRetrieveOptions?.expand !== void 0) {
      queryArray.push("expand=" + ArticleRetrieveOptions.expand.join(","));
    }
    return await super.sendRequest(
      {
        method: "GET",
        url: "articles/" + ArticleRetrieveParams.articleId + "?" + queryArray.join("&"),
        body: void 0,
        authenticationNotOptional: false
      },
      ArticleRetrieveOptions
    );
  }
  async update(ArticleUpdateParams, ArticleUpdateOptions) {
    return await super.sendRequest(
      {
        method: "PATCH",
        url: "articles/" + ArticleUpdateParams.articleId,
        body: {
          title: ArticleUpdateParams.title,
          subtitle: ArticleUpdateParams.subtitle,
          parentArticleId: ArticleUpdateParams.parentArticleId,
          markdownContent: ArticleUpdateParams.markdownContent,
          status: ArticleUpdateParams.status,
          languageCodeArray: ArticleUpdateParams.languageCodeArray
        },
        authenticationNotOptional: true
      },
      ArticleUpdateOptions
    );
  }
  async delete(ArticleDeleteParams, ArticleDeleteOptions) {
    return await super.sendRequest(
      {
        method: "DELETE",
        url: "articles/" + ArticleDeleteParams.articleId,
        authenticationNotOptional: true,
        body: void 0
      },
      ArticleDeleteOptions
    );
  }
  async search(ArticleSearchParams, ArticleSearchOptions) {
    const queryArray = [];
    if (ArticleSearchParams?.text !== void 0) {
      queryArray.push("text=" + ArticleSearchParams.text);
    }
    if (ArticleSearchParams?.slug !== void 0) {
      queryArray.push("slug=" + ArticleSearchParams.slug);
    }
    if (ArticleSearchOptions?.limit !== void 0) {
      queryArray.push("limit=" + ArticleSearchOptions.limit);
    }
    if (ArticleSearchOptions?.skip !== void 0) {
      queryArray.push("skip=" + ArticleSearchOptions.skip);
    }
    return await super.sendRequest(
      {
        method: "GET",
        url: "articles/search?" + queryArray.join("&"),
        body: void 0,
        authenticationNotOptional: false
      },
      ArticleSearchOptions
    );
  }
  async translate(ArticleTranslateParams, ArticleTranslateOptions) {
    return await super.sendRequest(
      {
        method: "PATCH",
        url: "articles/" + ArticleTranslateParams.articleId + "/translate",
        body: {
          languageCode: ArticleTranslateParams.languageCode
        },
        authenticationNotOptional: false
      },
      ArticleTranslateOptions
    );
  }
};

// src/chats/chats.service.ts
var ChatsService = class extends Service {
  constructor(config) {
    super(config);
  }
  async create(ChatCreateParams, ChatCreateOptions) {
    return await super.sendRequest(
      {
        method: "POST",
        url: "chats",
        body: {
          userId: ChatCreateParams.userId
        },
        authenticationNotOptional: true
      },
      ChatCreateOptions
    );
  }
  async list(ChatListParams, ChatListOptions) {
    const queryArray = [];
    if (ChatListOptions?.limit !== void 0) {
      queryArray.push("limit=" + ChatListOptions.limit);
    }
    if (ChatListParams?.userId !== void 0) {
      queryArray.push("userId=" + ChatListParams.userId);
    }
    if (ChatListOptions?.skip !== void 0) {
      queryArray.push("skip=" + ChatListOptions.skip);
    }
    return await super.sendRequest(
      {
        method: "GET",
        url: "chats?" + queryArray.join("&"),
        body: void 0,
        authenticationNotOptional: false
      },
      ChatListOptions
    );
  }
  async retrieve(ChatRetrieveParams, ChatRetrieveOptions) {
    const queryArray = [];
    return await super.sendRequest(
      {
        method: "GET",
        url: "chats/" + ChatRetrieveParams.chatId + "?" + queryArray.join("&"),
        body: void 0,
        authenticationNotOptional: false
      },
      ChatRetrieveOptions
    );
  }
  async update(ChatUpdateParams, ChatUpdateOptions) {
    return await super.sendRequest(
      {
        method: "PATCH",
        url: "chats/" + ChatUpdateParams.chatId,
        body: {
          title: ChatUpdateParams.title
        },
        authenticationNotOptional: true
      },
      ChatUpdateOptions
    );
  }
  async delete(ChatDeleteParams, ChatDeleteOptions) {
    return await super.sendRequest(
      {
        method: "DELETE",
        url: "chats/" + ChatDeleteParams.chatId,
        authenticationNotOptional: true,
        body: void 0
      },
      ChatDeleteOptions
    );
  }
  async complete(ChatUpdateParams, ChatUpdateOptions) {
    return await super.sendRequest(
      {
        method: "PATCH",
        url: "chats/" + ChatUpdateParams.chatId + "/complete",
        body: void 0,
        authenticationNotOptional: true
      },
      ChatUpdateOptions
    );
  }
};

// src/chatMessages/chatMessages.service.ts
var ChatMessagesService = class extends Service {
  constructor(config) {
    super(config);
  }
  async create(ChatMessageCreateParams, ChatMessageCreateOptions) {
    return await super.sendRequest(
      {
        method: "POST",
        url: "chatMessages",
        body: {
          chatId: ChatMessageCreateParams.chatId,
          message: ChatMessageCreateParams.message,
          userId: ChatMessageCreateParams.userId
        },
        authenticationNotOptional: true
      },
      ChatMessageCreateOptions
    );
  }
  async list(ChatMessageListParams, ChatMessageListOptions) {
    const queryArray = [];
    if (ChatMessageListParams.userId !== void 0) {
      queryArray.push("userId=" + ChatMessageListParams.userId);
    }
    if (ChatMessageListParams.chatId !== void 0) {
      queryArray.push("chatId=" + ChatMessageListParams.chatId);
    }
    if (ChatMessageListParams.limit !== void 0) {
      queryArray.push("limit=" + ChatMessageListParams.limit);
    }
    if (ChatMessageListParams.skip !== void 0) {
      queryArray.push("skip=" + ChatMessageListParams.skip);
    }
    return await super.sendRequest(
      {
        method: "GET",
        url: "chatMessages?" + queryArray.join("&"),
        body: void 0,
        authenticationNotOptional: true
      },
      ChatMessageListOptions
    );
  }
  async retrieve(ChatMessageRetrieveParams, ChatMessageRetrieveOptions) {
    const queryArray = [];
    return await super.sendRequest({
      method: "GET",
      url: "chatMessages/" + ChatMessageRetrieveParams.chatMessageId + "?" + queryArray.join("&"),
      body: void 0,
      authenticationNotOptional: true
    });
  }
};

// src/articleEmbeddings/articleEmbeddings.service.ts
var ArticleEmbeddingsService = class extends Service {
  constructor(config) {
    super(config);
  }
  async list(ArticleEmbeddingListParams, ArticleEmbeddingListOptions) {
    const queryArray = [];
    if (ArticleEmbeddingListOptions?.limit !== void 0) {
      queryArray.push("limit=" + ArticleEmbeddingListOptions.limit);
    }
    if (ArticleEmbeddingListParams?.articleId !== void 0) {
      queryArray.push("articleId=" + ArticleEmbeddingListParams.articleId);
    }
    if (ArticleEmbeddingListOptions?.skip !== void 0) {
      queryArray.push("skip=" + ArticleEmbeddingListOptions.skip);
    }
    return await super.sendRequest(
      {
        method: "GET",
        url: "articleEmbeddingEmbeddings?" + queryArray.join("&"),
        body: void 0,
        authenticationNotOptional: false
      },
      ArticleEmbeddingListOptions
    );
  }
  async retrieve(ArticleEmbeddingRetrieveParams, ArticleEmbeddingRetrieveOptions) {
    const queryArray = [];
    if (ArticleEmbeddingRetrieveOptions?.expand !== void 0) {
      queryArray.push(
        "expand=" + ArticleEmbeddingRetrieveOptions.expand.join(",")
      );
    }
    return await super.sendRequest(
      {
        method: "GET",
        url: "articleEmbeddingEmbeddings/" + ArticleEmbeddingRetrieveParams.articleEmbeddingId + "?" + queryArray.join("&"),
        body: void 0,
        authenticationNotOptional: false
      },
      ArticleEmbeddingRetrieveOptions
    );
  }
};

// src/types/types.ts
var LanguageCodeArray = [
  "en",
  // english
  "de",
  // german
  "fr",
  // french
  "nl",
  // dutch
  "it",
  // italian
  "cs",
  // czech
  "da",
  // danish
  "es",
  // spanish
  "et",
  // estonian
  "hr",
  // croatian
  "hu",
  // hungarian
  "no",
  // norwegian
  "pl",
  // polish
  "sv"
  // swedish
];
var CountryCodeArray = [
  "DE",
  // germany
  "AT",
  // austria
  "CH",
  // switzerland
  "LI",
  // liechtenstein
  "IT",
  // italy
  "FR",
  // france
  "NL",
  // netherlands
  "BE",
  // belgium
  "LU",
  // luxembourg
  "DK",
  // denmark
  "SE",
  // sweden
  "NO",
  // norway
  "FI",
  // finland
  "IS",
  // iceland
  "GB",
  // great britain
  "IE",
  // ireland
  "ES",
  // spain
  "PT",
  // portugal
  "GR",
  // greece
  "PL",
  // poland
  "CZ",
  // czech republic
  "SK",
  // slovakia
  "HU",
  // hungary
  "SI",
  // slovenia
  "HR",
  // croatia
  "BA",
  // bosnia and herzegovina
  "RS",
  // serbia
  "US",
  // united states
  "CA"
  // canada
];
var CurrencyCodeArray = [
  "eur",
  // euro
  "usd",
  // united states
  "gbp",
  // great britain
  "nok",
  // norway
  "sek",
  // sweden
  "chf",
  // switzerland
  "dkk",
  // denmark
  "pln",
  // poland
  "czk",
  // czech republic
  "huf",
  // hungary
  "bam",
  // bosnia and herzegovina
  "rsd",
  // serbia
  "isk",
  // iceland
  "cad"
  // canada
];
var ReadonlySectorArray = ["tourism", "mobility", "sport"];
var ReadonlyCategoryArray = [
  "bike",
  "car",
  "scooter",
  "boat",
  "moped"
];
var ReadonlyModalityArray = [
  "charge",
  "store",
  "share",
  "wash",
  "repair"
];
var ReadonlyIntegrationStateArray = ["full", "partial"];
var ConditionType = /* @__PURE__ */ ((ConditionType2) => {
  ConditionType2["Equal"] = "==";
  ConditionType2["NotEqual"] = "!=";
  ConditionType2["LessThan"] = "<";
  ConditionType2["GreaterThan"] = ">";
  ConditionType2["LessThanOrEqual"] = "<=";
  ConditionType2["GreaterThanOrEqual"] = ">=";
  return ConditionType2;
})(ConditionType || {});

// src/index.ts
var Juhuu = class {
  constructor(config) {
    this.sessions = new SessionService(config);
    this.links = new LinkService(config);
    this.users = new UsersService(config);
    this.payments = new PaymentsService(config);
    this.properties = new PropertiesService(config);
    this.points = new PointsService(config);
    this.devices = new DevicesService(config);
    this.deviceTemplates = new DeviceTemplatesService(config);
    this.locations = new LocationsService(config);
    this.terms = new TermsService(config);
    this.tariffs = new TariffsService(config);
    this.products = new ProductService(config);
    this.settings = new SettingsService(config);
    this.accountingAreas = new AccountingAreasService(config);
    this.connectors = new ConnectorsService(config);
    this.payouts = new PayoutsService(config);
    this.connectorMessages = new ConnectorMessagesService(config);
    this.sims = new SimsService(config);
    this.licenseTemplates = new LicenseTemplatesService(config);
    this.articles = new ArticlesService(config);
    this.chats = new ChatsService(config);
    this.chatMessages = new ChatMessagesService(config);
    this.articleEmbeddings = new ArticleEmbeddingsService(config);
  }
  /**
   * Top Level Resources
   */
  sessions;
  links;
  users;
  payments;
  properties;
  points;
  devices;
  deviceTemplates;
  locations;
  terms;
  tariffs;
  products;
  settings;
  accountingAreas;
  connectors;
  payouts;
  connectorMessages;
  sims;
  licenseTemplates;
  articles;
  chats;
  chatMessages;
  articleEmbeddings;
};
var JUHUU;
((JUHUU2) => {
  JUHUU2.ReadonlySectorArray = ["tourism", "mobility", "sport"];
  JUHUU2.ReadonlyTechnologyArray = ["analog", "digital"];
  JUHUU2.ReadonlyCategoryArray = [
    "bike",
    "car",
    "scooter",
    "boat",
    "moped"
  ];
  JUHUU2.ReadonlyModalityArray = [
    "charge",
    "store",
    "share",
    "wash",
    "repair"
  ];
  JUHUU2.ReadonlyIntegrationStateArray = ["full", "partial"];
})(JUHUU || (JUHUU = {}));
export {
  ConditionType,
  CountryCodeArray,
  CurrencyCodeArray,
  JUHUU,
  Juhuu,
  LanguageCodeArray,
  ReadonlyCategoryArray,
  ReadonlyIntegrationStateArray,
  ReadonlyModalityArray,
  ReadonlySectorArray
};
